/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import Table from '../table/Table';
import './ExcelTable.less';
import Loader, { ILoaderType } from '../Loader';

interface IProps {
  fileBlob: Blob;
}

function ExcelTable(props: IProps) {
  const { fileBlob } = props;

  const [loading, setLoading] = useState(true);
  const [sheetDataMap, setSheetDataMap] = useState<Record<string, any[]>>({});
  const [sheetHeaderMap, setSheetHeaderMap] = useState<Record<string, any[]>>(
    {}
  );

  useEffect(() => {
    let worker: Worker | undefined;
    try {
      // TODO: use absolute path here
      worker = new Worker(
        new URL('../../workers/excelWorker.ts', import.meta.url)
      );
      worker.onmessage = (event) => {
        const { sheetDataMap, headerMap, error } = event.data;

        if (error) {
          console.error('Error processing Excel file:', error);
          setLoading(false);
          return;
        }

        setSheetHeaderMap(headerMap);
        setSheetDataMap(sheetDataMap);
        setLoading(false);
      };
    } catch (error) {
      console.error('Failed to initialize Web Worker:', error);
      setLoading(false);
    }

    (async () => {
      setLoading(true);
      const arrayBuffer = await fileBlob.arrayBuffer();
      worker?.postMessage({ arrayBuffer });
    })();

    return () => {
      worker?.terminate();
    };
  }, [fileBlob]);

  const getColumns = (columnNames: any[]) =>
    columnNames.map((key: any) => ({
      id: key,
      dataKey: key,
      title: key.includes('__EMPTY') ? '' : key, // Show '' in case of empty excel column title
      renderColumn: (value: any) =>
        typeof value === 'boolean' ? value.toString() : value
    }));

  return (
    <div className="excel-table">
      {loading ? (
        <Loader type={ILoaderType.SKELETON} skeletonConfig={{ rows: 10 }} />
      ) : (
        <Tabs
          defaultActiveKey="0"
          tabPosition="top"
          items={Object.keys(sheetDataMap).map((sheetName) => {
            const data = sheetDataMap[sheetName];
            const columnNames = sheetHeaderMap[sheetName];
            return {
              label: sheetName,
              key: sheetName,
              children: (
                <Table
                  key={`${sheetName}`}
                  data={data}
                  loading={loading}
                  columns={data.length > 0 ? getColumns(columnNames) : []}
                  uniqueRowId="id"
                  pagination={{}}
                />
              )
            };
          })}
        />
      )}
    </div>
  );
}

export default ExcelTable;
